import {
  DecoratorNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type DOMConversion,
  type DOMConversionOutput,
  Spread,
} from 'lexical';
import React from 'react';

export type ImagePayload = {
  src: string;
  altText?: string | null;
  title?: string | null;
  width?: number | string | null;
  height?: number | string | null;
};

export type SerializedImageNode = Spread<
  {
    type: 'image';
    version: 1;
    src: string;
    altText?: string | null;
    title?: string | null;
    width?: number | string | null;
    height?: number | string | null;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string | null | undefined;
  __title: string | null | undefined;
  __width: number | string | null | undefined;
  __height: number | string | null | undefined;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      {
        src: node.__src,
        altText: node.__altText ?? null,
        title: node.__title ?? null,
        width: node.__width ?? null,
        height: node.__height ?? null,
      },
      node.__key,
    );
  }

  constructor(payload: ImagePayload, key?: NodeKey) {
    super(key);
    this.__src = payload.src;
    this.__altText = payload.altText ?? null;
    this.__title = payload.title ?? null;
    this.__width = payload.width ?? null;
    this.__height = payload.height ?? null;
  }

  exportJSON(): SerializedImageNode {
    return {
      type: 'image',
      version: 1,
      src: this.__src,
      altText: this.__altText ?? null,
      title: this.__title ?? null,
      width: this.__width ?? null,
      height: this.__height ?? null,
    };
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, title, width, height } = serializedNode;
    return $createImageNode({ src, altText, title, width, height });
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    const {
      __src: src,
      __altText: altText,
      __title: title,
      __width: width,
      __height: height,
    } = this;
    const style: React.CSSProperties = {};
    if (width != null) style.width = typeof width === 'number' ? `${width}px` : width;
    if (height != null) style.height = typeof height === 'number' ? `${height}px` : height;
    return (
      <img
        src={src}
        alt={altText ?? ''}
        title={title ?? undefined}
        style={style}
        draggable={false}
      />
    );
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (domNode: Node): DOMConversion | null => {
        if (domNode instanceof HTMLImageElement) {
          return {
            conversion: convertImageElement,
            priority: 0,
          };
        }
        return null;
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    if (this.__altText) element.setAttribute('alt', this.__altText);
    if (this.__title) element.setAttribute('title', this.__title);
    if (this.__width != null) element.setAttribute('width', String(this.__width));
    if (this.__height != null) element.setAttribute('height', String(this.__height));
    return { element };
  }
}

function convertImageElement(domNode: Node): DOMConversionOutput {
  if (!(domNode instanceof HTMLImageElement)) return { node: null };
  const src = domNode.getAttribute('src') || domNode.src || '';
  const altText = domNode.getAttribute('alt');
  const title = domNode.getAttribute('title');
  const widthAttr = domNode.getAttribute('width');
  const heightAttr = domNode.getAttribute('height');
  const width = widthAttr ? (isNaN(Number(widthAttr)) ? widthAttr : Number(widthAttr)) : null;
  const height = heightAttr ? (isNaN(Number(heightAttr)) ? heightAttr : Number(heightAttr)) : null;
  const node = $createImageNode({ src, altText, title, width, height });
  return { node };
}

export function $createImageNode(payload: ImagePayload): ImageNode {
  return new ImageNode(payload);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
